import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCamera,
  faSave,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faTwitter, 
  faFacebook, 
  faYoutube,
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { 
  doc, 
  getDoc, 
  updateDoc
} from 'firebase/firestore';
import { updateBandDetails } from '../../utils/BandUtils';
import { UserContext } from '../../context/UserContext';
import styles from './BandEditModal.module.css';
import ImageCropper from '../common/ImageCropper';
import { uploadBandImage } from '../../utils/ImageUtils';
import defaultBandPic from '../../assets/Images/defaultUserProfilePicture.png';
import defaultCoverPhoto from '../../assets/Images/defaultUserCoverPicture.png';
import appleMusicLogo from '../../assets/Images/applemusicicon.png';
import spotifyLogo from '../../assets/Images/spotifyicon.png';

// Define genre options (alphabetically sorted)
const GENRE_OPTIONS = [
  'Alternative', 'Blues', 'Classical', 'Country', 'Dance', 'Electronic',
  'Folk', 'Funk', 'Gospel', 'Grunge', 'Hip Hop', 'Indie', 'Jazz', 'Latin',
  'Metal', 'New Age', 'Opera', 'Pop', 'Progressive', 'Psychedelic', 'Punk',
  'R&B', 'Rap', 'Reggae', 'Reggaeton', 'Rock', 'Salsa', 'Ska', 'Soul',
  'Techno', 'Trance', 'World Music'
].sort();

const BandEditModal = ({ isOpen, onClose, band, onSuccess }) => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [bandData, setBandData] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Form data state
  const [formData, setFormData] = useState({
    bandFullName: '',
    bandBio: '',
    bandEmail: '',
    bandWebsite: '',
    city: '',
    state: '',
    country: '',
    bandGenres: [],
    socialLinks: {
      instagramUrl: '',
      twitterUrl: '',
      facebookUrl: '',
      youtubeUrl: '',
      linkedInUrl: '',
      spotifyUrl: '',
      appleMusicUrl: ''
    }
  });

  // Fetch band data when modal opens
  useEffect(() => {
    const fetchBandData = async () => {
      if (!isOpen || !band?.id) return;
      
      try {
        setIsLoading(true);
        const db = getFirestoreInstance();
        const bandDoc = await getDoc(doc(db, 'bands', band.id));
        
        if (bandDoc.exists()) {
          const data = bandDoc.data();
          setBandData(data);
          setFormData({
            bandFullName: data.bandFullName || '',
            bandBio: data.bandBio || '',
            bandEmail: data.bandEmail || '',
            bandWebsite: data.bandWebsite || '',
            city: data.city || '',
            state: data.state || '',
            country: data.country || '',
            bandGenres: data.bandGenres || [],
            socialLinks: data.socialLinks || {
              instagramUrl: '',
              twitterUrl: '',
              facebookUrl: '',
              youtubeUrl: '',
              linkedInUrl: '',
              spotifyUrl: '',
              appleMusicUrl: ''
            }
          });
          
          // Set image previews
          setProfilePicture(prev => ({
            ...prev,
            preview: data.bandProfilePictureUrl || defaultBandPic
          }));
          setCoverPhoto(prev => ({
            ...prev,
            preview: data.bandCoverPictureUrl || defaultCoverPhoto
          }));
        } else {
          setErrors({ general: 'Band not found' });
        }
      } catch (error) {
        console.error('Error fetching band data:', error);
        setErrors({ general: 'Failed to load band data' });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBandData();
  }, [isOpen, band?.id]);

  // Image states
  const [showProfileCropper, setShowProfileCropper] = useState(false);
  const [showCoverCropper, setShowCoverCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropData, setCropData] = useState(null);
  const [profilePicture, setProfilePicture] = useState({
    file: null,
    preview: null,
    cropData: null
  });
  
  const [coverPhoto, setCoverPhoto] = useState({
    file: null,
    preview: null,
    cropData: null
  });

  // Form input handlers
  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
    
    // Clear error for this field
    if (errors[`socialLinks.${platform}`]) {
      setErrors(prev => ({
        ...prev,
        [`socialLinks.${platform}`]: null
      }));
    }
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => {
      const genres = prev.bandGenres.includes(genre)
        ? prev.bandGenres.filter(g => g !== genre)
        : [...prev.bandGenres, genre];
      return { ...prev, bandGenres: genres };
    });
  };

  // Image handling functions
  const handleImageSelect = (e, isProfile) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const preview = URL.createObjectURL(file);
      setSelectedImage({
        file,
        preview,
        isProfile
      });
      if (isProfile) {
        setShowProfileCropper(true);
      } else {
        setShowCoverCropper(true);
      }
    }
  };

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCropData(croppedAreaPixels);
  };

  const handleSaveCrop = async () => {
    if (!selectedImage || !cropData) {
      console.error('Missing required data for crop');
      return;
    }
  
    try {
      setIsLoading(true);
      const isProfile = selectedImage.isProfile;
      const imageFile = selectedImage.file;
  
      // Create a local preview URL instead of uploading
      const preview = URL.createObjectURL(imageFile);
  
      // Just update the state with file, crop data, and preview
      if (isProfile) {
        setProfilePicture(prev => ({
          ...prev,
          file: imageFile,
          preview: preview,
          cropData: cropData
        }));
        setShowProfileCropper(false);
      } else {
        setCoverPhoto(prev => ({
          ...prev,
          file: imageFile,
          preview: preview,
          cropData: cropData
        }));
        setShowCoverCropper(false);
      }
  
      if (selectedImage.preview) {
        URL.revokeObjectURL(selectedImage.preview);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setErrors(prev => ({
        ...prev,
        image: 'Failed to process image'
      }));
    } finally {
      setIsLoading(false);
      setSelectedImage(null);
      setCropData(null);
    }
  };

  // Validation functions
  const validateBasicInfo = () => {
    const newErrors = {};
    
    if (!formData.bandFullName) {
      newErrors.bandFullName = 'Band name is required';
    }
    
    if (formData.bandGenres.length === 0) {
      newErrors.bandGenres = 'Please select at least one genre';
    }
    
    setErrors(prev => ({...prev, ...newErrors}));
    return Object.keys(newErrors).length === 0;
  };

  const validateContactInfo = () => {
    const newErrors = {};
    const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
    
    if (formData.bandEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.bandEmail)) {
      newErrors.bandEmail = 'Please enter a valid email address';
    }
    
    if (formData.bandWebsite && !urlRegex.test(formData.bandWebsite)) {
      newErrors.bandWebsite = 'Please enter a valid website URL';
    }
    
    setErrors(prev => ({...prev, ...newErrors}));
    return Object.keys(newErrors).length === 0;
  };

  const validateSocialLinks = () => {
    const newErrors = {};
    const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
    
    Object.entries(formData.socialLinks).forEach(([key, value]) => {
      if (value && !urlRegex.test(value)) {
        newErrors[`socialLinks.${key}`] = 'Please enter a valid URL';
      }
    });
    
    setErrors(prev => ({...prev, ...newErrors}));
    return Object.keys(newErrors).length === 0;
  };

  // Tab navigation
  const handleTabChange = (tab) => {
    // Validate current tab before switching
    let isValid = true;
    
    switch (activeTab) {
      case 'basic':
        isValid = validateBasicInfo();
        break;
      case 'contact':
        isValid = validateContactInfo();
        break;
      case 'social':
        isValid = validateSocialLinks();
        break;
    }
    
    if (isValid) {
      setActiveTab(tab);
    }
  };

  // Save changes
  const handleSave = async () => {
    try {
      // Validate all sections
      const isBasicValid = validateBasicInfo();
      const isContactValid = validateContactInfo();
      const isSocialValid = validateSocialLinks();
      
      if (!isBasicValid || !isContactValid || !isSocialValid) {
        // Switch to the tab with errors
        if (!isBasicValid) {
          setActiveTab('basic');
        } else if (!isContactValid) {
          setActiveTab('contact');
        } else if (!isSocialValid) {
          setActiveTab('social');
        }
        return;
      }
      
      setIsLoading(true);
      
      // Prepare update data
      const updateData = {
        ...formData,
        lastUpdated: new Date()
      };
      
      // Handle image uploads if there are new images
      if (profilePicture.file) {
        const profileUrl = await uploadBandImage(
          profilePicture.file,
          profilePicture.cropData,
          userData.userId,
          band.id,
          true // isProfile
        );
        updateData.bandProfilePictureUrl = profileUrl;
      }

      if (coverPhoto.file) {
        const coverUrl = await uploadBandImage(
          coverPhoto.file,
          coverPhoto.cropData,
          userData.userId,
          band.id,
          false // not profile
        );
        updateData.bandCoverPictureUrl = coverUrl;
      }
      
      // Update band document with all data including new image URLs
      await updateBandDetails(band.id, updateData, userData);
      
      // Call onSuccess if provided
      if (onSuccess) {
        onSuccess(band.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating band:', error);
      setErrors(prev => ({
        ...prev,
        general: error.message || 'Failed to update band details'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // View band profile
  const handleViewProfile = () => {
    if (bandData?.bandUserName) {
      navigate(`/bands/${bandData.bandUserName}`);
      onClose();
    }
  };

  // Find admin members
  const getAdminMembers = () => {
    return bandData?.members?.filter(member => member.role === 'admin') || [];
  };

  // Find regular members
  const getRegularMembers = () => {
    return bandData?.members?.filter(member => member.role === 'member') || [];
  };

  if (!isOpen || !bandData) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Edit Band: {bandData.bandFullName}</h2>
          <div className={styles.headerActions}>
            <button 
              className={styles.viewProfileButton} 
              onClick={handleViewProfile}
              title="View band profile"
            >
              View Profile
            </button>
            <button 
              className={styles.closeButton} 
              onClick={onClose}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          {errors.general && (
            <div className={styles.generalError}>
              <FontAwesomeIcon icon={faInfoCircle} />
              {errors.general}
            </div>
          )}

          {/* Image Section */}
          <div className={styles.imageSection}>
            <div className={styles.coverPhotoContainer}>
              <img 
                src={coverPhoto.preview || defaultCoverPhoto}
                alt="Cover"
                className={styles.coverPhoto}
              />
              <button 
                type="button"
                className={styles.editCoverPhotoButton}
                onClick={(e) => handleImageSelect(e, false)}
                title="Change cover photo"
              >
                <FontAwesomeIcon icon={faCamera} />
              </button>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageSelect(e, false)}
                className={styles.hiddenInput}
                id="coverPhotoInput"
              />
            </div>

            <div className={styles.profilePictureContainer}>
              <img 
                src={profilePicture.preview || defaultBandPic}
                alt="Profile"
                className={styles.profilePicture}
              />
              <button 
                type="button"
                className={styles.editProfilePictureButton}
                onClick={(e) => handleImageSelect(e, true)}
                title="Change profile photo"
              >
                <FontAwesomeIcon icon={faCamera} />
              </button>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageSelect(e, true)}
                className={styles.hiddenInput}
                id="profilePicInput"
              />
            </div>
          </div>

          {/* Tab Navigation */}
          <div className={styles.tabNavigation}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'basic' ? styles.active : ''}`}
              onClick={() => handleTabChange('basic')}
            >
              Basic Info
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'contact' ? styles.active : ''}`}
              onClick={() => handleTabChange('contact')}
            >
              Contact Info
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'social' ? styles.active : ''}`}
              onClick={() => handleTabChange('social')}
            >
              Social Media
            </button>
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className={styles.tabPanel}>
                <div className={styles.field}>
                  <label>Band Name *</label>
                  <input
                    type="text"
                    value={formData.bandFullName}
                    onChange={(e) => handleInputChange('bandFullName', e.target.value)}
                    className={errors.bandFullName ? styles.fieldError : ''}
                  />
                  {errors.bandFullName && (
                    <div className={styles.errorMessage}>{errors.bandFullName}</div>
                  )}
                </div>

                <div className={styles.field}>
                  <label>Bio</label>
                  <textarea
                    value={formData.bandBio}
                    onChange={(e) => handleInputChange('bandBio', e.target.value)}
                    maxLength={500}
                    rows={4}
                  />
                  <div className={styles.helperText}>
                    {formData.bandBio.length}/500 characters
                  </div>
                </div>

                <div className={styles.field}>
                  <label>Genres *</label>
                  {errors.bandGenres && (
                    <div className={styles.errorMessage}>{errors.bandGenres}</div>
                  )}
                  <div className={styles.genreGrid}>
                    {GENRE_OPTIONS.map(genre => (
                      <button
                        key={genre}
                        type="button"
                        className={`${styles.genreButton} ${
                          formData.bandGenres.includes(genre) ? styles.selected : ''
                        }`}
                        onClick={() => handleGenreToggle(genre)}
                      >
                        {genre}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information Tab */}
            {activeTab === 'contact' && (
              <div className={styles.tabPanel}>
                <div className={styles.field}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.bandEmail}
                    onChange={(e) => handleInputChange('bandEmail', e.target.value)}
                    className={errors.bandEmail ? styles.fieldError : ''}
                  />
                  {errors.bandEmail && (
                    <div className={styles.errorMessage}>{errors.bandEmail}</div>
                  )}
                  <div className={styles.helperText}>
                    This email will be visible on your band profile
                  </div>
                </div>

                <div className={styles.field}>
                  <label>Website</label>
                  <input
                    type="url"
                    value={formData.bandWebsite}
                    onChange={(e) => handleInputChange('bandWebsite', e.target.value)}
                    className={errors.bandWebsite ? styles.fieldError : ''}
                  />
                  {errors.bandWebsite && (
                    <div className={styles.errorMessage}>{errors.bandWebsite}</div>
                  )}
                </div>

                <div className={styles.locationFields}>
                  <div className={styles.field}>
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label>Country</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Social Media Tab */}
            {activeTab === 'social' && (
              <div className={styles.tabPanel}>
                <div className={styles.field}>
                  <div className={styles.socialInputGroup}>
                    <FontAwesomeIcon icon={faInstagram} className={styles.socialIcon} />
                    <input
                      type="url"
                      value={formData.socialLinks.instagramUrl || ''}
                      onChange={(e) => handleSocialLinkChange('instagramUrl', e.target.value)}
                      placeholder="Instagram URL"
                      className={errors['socialLinks.instagramUrl'] ? styles.fieldError : ''}
                    />
                  </div>
                  {errors['socialLinks.instagramUrl'] && (
                    <div className={styles.errorMessage}>{errors['socialLinks.instagramUrl']}</div>
                  )}
                </div>

                <div className={styles.field}>
                  <div className={styles.socialInputGroup}>
                    <FontAwesomeIcon icon={faTwitter} className={styles.socialIcon} />
                    <input
                      type="url"
                      value={formData.socialLinks.twitterUrl || ''}
                      onChange={(e) => handleSocialLinkChange('twitterUrl', e.target.value)}
                      placeholder="Twitter URL"
                      className={errors['socialLinks.twitterUrl'] ? styles.fieldError : ''}
                    />
                  </div>
                  {errors['socialLinks.twitterUrl'] && (
                    <div className={styles.errorMessage}>{errors['socialLinks.twitterUrl']}</div>
                  )}
                </div>

                <div className={styles.field}>
                  <div className={styles.socialInputGroup}>
                    <FontAwesomeIcon icon={faFacebook} className={styles.socialIcon} />
                    <input
                      type="url"
                      value={formData.socialLinks.facebookUrl || ''}
                      onChange={(e) => handleSocialLinkChange('facebookUrl', e.target.value)}
                      placeholder="Facebook URL"
                      className={errors['socialLinks.facebookUrl'] ? styles.fieldError : ''}
                    />
                  </div>
                  {errors['socialLinks.facebookUrl'] && (
                    <div className={styles.errorMessage}>{errors['socialLinks.facebookUrl']}</div>
                  )}
                </div>

                <div className={styles.field}>
                  <div className={styles.socialInputGroup}>
                    <FontAwesomeIcon icon={faYoutube} className={styles.socialIcon} />
                    <input
                      type="url"
                      value={formData.socialLinks.youtubeUrl || ''}
                      onChange={(e) => handleSocialLinkChange('youtubeUrl', e.target.value)}
                      placeholder="YouTube URL"
                      className={errors['socialLinks.youtubeUrl'] ? styles.fieldError : ''}
                    />
                  </div>
                  {errors['socialLinks.youtubeUrl'] && (
                    <div className={styles.errorMessage}>{errors['socialLinks.youtubeUrl']}</div>
                  )}
                </div>

                <div className={styles.field}>
                  <div className={styles.socialInputGroup}>
                    <div className={styles.socialIconContainer}>
                      <img src={spotifyLogo} alt="Spotify" className={styles.brandSocialIcon} />
                    </div>
                    <input
                      type="url"
                      value={formData.socialLinks.spotifyUrl || ''}
                      onChange={(e) => handleSocialLinkChange('spotifyUrl', e.target.value)}
                      placeholder="Spotify URL"
                      className={errors['socialLinks.spotifyUrl'] ? styles.fieldError : ''}
                    />
                  </div>
                  {errors['socialLinks.spotifyUrl'] && (
                    <div className={styles.errorMessage}>{errors['socialLinks.spotifyUrl']}</div>
                  )}
                </div>

                <div className={styles.field}>
                  <div className={styles.socialInputGroup}>
                    <div className={styles.socialIconContainer}>
                      <img src={appleMusicLogo} alt="Apple Music" className={styles.brandSocialIcon} />
                    </div>
                    <input
                      type="url"
                      value={formData.socialLinks.appleMusicUrl || ''}
                      onChange={(e) => handleSocialLinkChange('appleMusicUrl', e.target.value)}
                      placeholder="Apple Music URL"
                      className={errors['socialLinks.appleMusicUrl'] ? styles.fieldError : ''}
                    />
                  </div>
                  {errors['socialLinks.appleMusicUrl'] && (
                    <div className={styles.errorMessage}>{errors['socialLinks.appleMusicUrl']}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button 
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={styles.buttonSpinner}></span>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Image Croppers */}
      {showProfileCropper && selectedImage && (
        <ImageCropper
          image={selectedImage.preview}
          onCropComplete={handleCropComplete}
          onSave={handleSaveCrop}
          onCancel={() => {
            setShowProfileCropper(false);
            if (selectedImage.preview) {
              URL.revokeObjectURL(selectedImage.preview);
            }
            setSelectedImage(null);
            setCropData(null);
          }}
          aspect={1}
          cropShape="round"
          minWidth={500}
        />
      )}

      {showCoverCropper && selectedImage && (
        <ImageCropper
          image={selectedImage.preview}
          onCropComplete={handleCropComplete}
          onSave={handleSaveCrop}
          onCancel={() => {
            setShowCoverCropper(false);
            if (selectedImage.preview) {
              URL.revokeObjectURL(selectedImage.preview);
            }
            setSelectedImage(null);
            setCropData(null);
          }}
          aspect={16/9}
          minWidth={1200}
          minHeight={675}
        />
      )}
    </div>
  );
};

export default BandEditModal;